/**
 * The round-two half of the in-memory `GameApi`: intel on sale, specialist stock, perk training,
 * the Daily Drop and the records. Simple rules held in memory, so a screen test can buy a card
 * and see the RP fall, or fly the daily and find it on the table.
 */

import type { Deployment, GameApi } from '../../../meta/api';
import type {
  ArchetypeRecord, DailyEntry, IntelOffer, Operation, PerkId, Profile, RangerRecord, Records, SpecialItem,
} from '../../../meta/types';
import type { ArchetypeId, Gear, IntelId, MissionParams } from '../../../sim/types';
import { ARCHETYPES } from '../archetypes';

/** What the fake holds beyond the `Profile`: the contract keeps these behind methods. */
export interface FakeExtras {
  stock: Partial<Record<SpecialItem, number>>;
  daily: DailyEntry[];
  byArchetype: Partial<Record<ArchetypeId, ArchetypeRecord>>;
}

export type ExtraMethods = Pick<
  GameApi, 'intelOffers' | 'buyIntel' | 'stock' | 'trainPerk' | 'daily' | 'deployDaily' | 'records'
>;

/**
 * The three cards every mission node sells here (04 §8). The last is priced beyond the fixture
 * operation's 30 RP on purpose, so there is always a card the shop has to refuse.
 */
export const FAKE_INTEL: ReadonlyArray<[id: IntelId, price: number]> = [
  ['recon-photos', 6],
  ['patrol-schedule', 5],
  ['alternate-lz', 40],
];

export const RANGER_OF_THE_DAY = 'PFC "DAWN" HOLLIS';
export const PISTOL_ROUNDS = 14;

export function emptyExtras(): FakeExtras {
  return { stock: {}, daily: [], byArchetype: {} };
}

/** A flown table, some stock and a few bests: what the fixture routes show. */
export function showcaseExtras(): FakeExtras {
  return {
    stock: { smoke: 3, nvg: 1, 'wire-cutters': 1, 'suppressed-pistol': 0 },
    daily: [
      {
        date: '2026-09-17', archetype: 'pipeline', biome: 'temperate', rangerName: 'PFC "ASH" TURNER',
        outcome: 'extracted', score: 3120, secondsUsed: 412,
      },
      {
        date: '2026-09-18', archetype: 'hostages', biome: 'desert', rangerName: 'PFC "KITE" BAUER',
        outcome: 'kia', score: 640, secondsUsed: 233,
      },
    ],
    byArchetype: {
      'munitions-depot': { attempts: 5, completions: 4, bestScore: 3400, bestSeconds: 287 },
      pipeline: { attempts: 3, completions: 1, bestScore: 3120, bestSeconds: 412 },
      hostages: { attempts: 2, completions: 0, bestScore: 640, bestSeconds: null },
      // The expansion (10 §2): one done, one flown and lost, one of the people missions.
      'blow-bridge': { attempts: 2, completions: 1, bestScore: 2650, bestSeconds: 398 },
      wiretap: { attempts: 1, completions: 0, bestScore: 310, bestSeconds: null },
      'downed-pilot': { attempts: 4, completions: 2, bestScore: 2980, bestSeconds: 471 },
    },
  };
}

function dateHash(dateUtc: string): number {
  let h = 17;
  for (let i = 0; i < dateUtc.length; i++) h = (h * 31 + dateUtc.charCodeAt(i)) % 0x7fffffff;
  return h;
}

/** What a daily may be: like the real one, never a mission with no drop (the Daily DROP flies a fixed kit). */
const DAILY_ARCHETYPES = ARCHETYPES.filter((a) => !a.facts.startCaptured);

/** Same date, same mission: archetype on its native ground, picked from the date's hash. */
export function dailyParams(dateUtc: string): MissionParams {
  const h = dateHash(dateUtc);
  const info = DAILY_ARCHETYPES[h % DAILY_ARCHETYPES.length]
    ?? { id: 'munitions-depot' as const, biome: 'desert' as const };
  return {
    archetype: info.id,
    biome: info.biome,
    timeOfDay: h % 3 === 0 ? 'night' : 'day',
    weather: 'clear',
    threat: 3 + (h % 4),
    seed: h || 1,
    modifiers: h % 3 === 0 ? ['night-op'] : [],
    intel: [],
  };
}

/** Takes what `gear` names out of the stock; throws (like the real `deploy`) when it is not there. */
export function takeGear(extras: FakeExtras, gear: Partial<Gear> | undefined): void {
  if (gear === undefined) return;
  const wanted: Array<[SpecialItem, number]> = [
    ['smoke', gear.smoke ?? 0],
    ['nvg', gear.nvg === true ? 1 : 0],
    ['wire-cutters', gear.wireCutters === true ? 1 : 0],
    ['suppressed-pistol', (gear.pistolRounds ?? 0) > 0 ? 1 : 0],
    ['ghillie', gear.ghillie === true ? 1 : 0],
  ];
  for (const [item, n] of wanted) {
    if (n > (extras.stock[item] ?? 0)) throw new Error(`NOT IN STOCK: ${item.toUpperCase()}`);
  }
  for (const [item, n] of wanted) if (n > 0) extras.stock[item] = (extras.stock[item] ?? 0) - n;
}

export interface ExtraDeps {
  data: Profile;
  extras: FakeExtras;
  calls: string[];
  op(): Operation;
  find(id: string): RangerRecord;
}

export function createExtraMethods({ data, extras, calls, op, find }: ExtraDeps): ExtraMethods {
  const missionNode = (nodeId: string) => {
    const node = data.operation?.nodes.find((n) => n.id === nodeId);
    return node !== undefined && node.params !== null ? { node, params: node.params } : null;
  };

  return {
    intelOffers(nodeId): IntelOffer[] {
      calls.push('intelOffers');
      const hit = missionNode(nodeId);
      if (hit === null) return [];
      return FAKE_INTEL.map(([id, price]) => ({ id, price, owned: hit.params.intel.includes(id) }));
    },
    buyIntel(nodeId, intel) {
      calls.push('buyIntel');
      const o = op();
      const hit = missionNode(nodeId);
      const offer = FAKE_INTEL.find(([id]) => id === intel);
      if (hit === null || offer === undefined) throw new Error('THAT INTEL IS NOT ON OFFER HERE');
      if (hit.params.intel.includes(intel)) throw new Error('THAT INTEL IS ALREADY HELD');
      if (offer[1] > o.rp) throw new Error(`NOT ENOUGH REQUISITION: ${offer[1]} RP NEEDED, ${o.rp} HELD`);
      o.rp -= offer[1];
      hit.params.intel.push(intel);
    },
    stock() {
      calls.push('stock');
      return { ...extras.stock };
    },
    trainPerk(rangerId, perk: PerkId) {
      calls.push('trainPerk');
      const o = op();
      const teachable = o.trainablePerks ?? [];
      if (!teachable.includes(perk)) throw new Error('THAT PERK IS NOT ON OFFER');
      const r = find(rangerId);
      if (r.perks.includes(perk)) throw new Error('HE ALREADY HAS THAT PERK');
      r.perks.push(perk);
      o.trainablePerks = teachable.filter((p) => p !== perk);
      return r;
    },
    daily(dateUtc) {
      calls.push('daily');
      const played = extras.daily.find((e) => e.date === dateUtc) ?? null;
      return { date: dateUtc, params: dailyParams(dateUtc), rangerName: RANGER_OF_THE_DAY, played };
    },
    deployDaily(dateUtc): Deployment {
      calls.push('deployDaily');
      if (extras.daily.some((e) => e.date === dateUtc)) throw new Error("TODAY'S DROP HAS ALREADY BEEN FLOWN");
      const params = dailyParams(dateUtc);
      // Played from the moment the aircraft leaves: quitting is not a second attempt.
      extras.daily.push({
        date: dateUtc, archetype: params.archetype, biome: params.biome, rangerName: RANGER_OF_THE_DAY,
        outcome: 'scrubbed', score: 0, secondsUsed: 0,
      });
      const carried = { magazines: 4, roundsInMag: 30, grenades: 3, laws: 1, bombs: 1, firstAid: 1 };
      return { params, rangerId: `daily:${dateUtc}`, loadout: { carried, pods: [] }, mods: {}, mode: 'daily' };
    },
    records(): Records {
      calls.push('records');
      const everyone = [...data.hall, ...data.retired, ...data.memorial.map((m) => m.ranger)];
      const name = (r: RangerRecord): string => `${r.firstName} ${r.surname}`;
      const longest = [...everyone].sort((a, b) => b.record.missions - a.record.missions)[0];
      const decorated = [...everyone].sort((a, b) => b.medals.length - a.medals.length)[0];
      return {
        byArchetype: extras.byArchetype,
        longestServing: longest === undefined ? null : { name: name(longest), missions: longest.record.missions },
        mostDecorated: decorated === undefined ? null : { name: name(decorated), medals: decorated.medals.length },
        bestStreak: Object.keys(extras.byArchetype).length === 0 ? 0 : 6,
        operationsWon: data.operationsWon,
        rangersLost: data.memorial.length,
        daily: [...extras.daily],
      };
    },
  };
}
